import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ExternalLink,
  HardHat,
  Wrench,
  Trash2,
  Plus,
  TrendingUp,
  TrendingDown,
  Receipt,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import {
  formatUsd,
  formatPct,
  marginLabel,
  groupLaborByDate,
  deleteMaterialLogEntry,
} from '@/lib/jobCostingData';
import { toast } from 'sonner';

const TONE_CLASSES = {
  success:
    'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30',
  warning:
    'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30',
  danger:
    'bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30',
};

const Section = ({ title, children, right }) => (
  <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
    <CardContent className="p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          {title}
        </div>
        {right}
      </div>
      {children}
    </CardContent>
  </Card>
);

const StatBlock = ({ label, value, tone = 'default', testId }) => {
  const toneCls = {
    default: 'text-foreground',
    success: 'text-[hsl(var(--success))]',
    warning: 'text-[hsl(var(--warning))]',
    danger: 'text-[hsl(var(--destructive))]',
  }[tone];
  return (
    <div className="rounded-md border border-border bg-[hsl(var(--muted))]/40 px-3 py-2">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </div>
      <div
        className={cn('mt-1 text-base font-semibold tabular-nums', toneCls)}
        data-testid={testId}
      >
        {value}
      </div>
    </div>
  );
};

export const JobCostingDetailSheet = ({
  open,
  onOpenChange,
  row,
  onUpdated,
  onLogMaterial,
  fullPage = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [deletingId, setDeletingId] = useState(null);

  if (!row) return null;

  // Translate the margin label suffix for the header badge (Healthy / Watch / At Risk).
  const marginLabelMap = { Healthy: 'healthy', Watch: 'watch', 'At Risk': 'risk' };
  const localizedMargin = (pct) => {
    const raw = marginLabel(pct);
    const key = marginLabelMap[raw] || 'watch';
    return t(`jobCosting.marginLabels.${key}`, { defaultValue: raw });
  };

  const laborGroups = groupLaborByDate(row.labor.entries || []);
  const materials = row.materials?.entries || [];

  const handleDelete = async (entryId) => {
    setDeletingId(entryId);
    try {
      const updated = await deleteMaterialLogEntry(row.job, entryId);
      toast.success(t('jobCosting.materials.removedToast'));
      onUpdated?.(updated);
    } catch (e) {
      toast.error(e?.message || t('jobCosting.materials.removeFailed'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleOpenJob = () => {
    navigate(`/dashboard/active-jobs?jobId=${encodeURIComponent(row.job_id)}`);
    onOpenChange?.(false);
  };

  // Full page (wide screens, John 2026-10-09: "needs to be changed to full
  // screen"): same content, no Sheet. SheetTitle/SheetDescription are Radix
  // Dialog parts and throw outside a Sheet, so the inline page uses plain
  // elements in their place.
  const Header = fullPage ? 'div' : SheetHeader;
  const Title = fullPage ? 'h2' : SheetTitle;

  const content = (
    <>
        <Header
          className={cn(
            'px-5 sm:px-6 pt-5 pb-4 border-b border-[hsl(var(--hairline))] bg-[hsl(var(--card))]',
            fullPage ? null : 'pr-12 sm:pr-14'
          )}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                {t('jobCosting.detail.eyebrow')}
              </div>
              <Title
                className={cn(
                  'mt-1 text-xl sm:text-2xl font-semibold tracking-tight text-foreground',
                  fullPage ? 'break-words' : 'truncate'
                )}
                data-testid="costing-detail-job-name"
              >
                {row.name}
              </Title>
              {fullPage ? null : (
                <SheetDescription className="sr-only">
                  {row.name}
                </SheetDescription>
              )}
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                <Badge
                  variant="outline"
                  className={cn(
                    'uppercase text-[10px] tracking-[0.16em]',
                    TONE_CLASSES[row.tone]
                  )}
                >
                  {localizedMargin(row.marginPct)} {'\u00b7'} {formatPct(row.marginPct)}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {row.client || t('jobCosting.noClient')}
                </span>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleOpenJob}
              className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs shrink-0"
              data-testid="costing-detail-open-job"
            >
              <ExternalLink size={12} className="mr-1.5" />
              {t('jobCosting.detail.openJob')}
            </Button>
          </div>
        </Header>

        {/* Wide-screen sticky totals rail (xl+, >=1280px): the cost/margin
            summary (Contract vs Actual + Variance) pins in a right column
            that stays in view while Labor/Materials line items scroll below
            it — order-2/order-1 keeps the DOM (and the collapsed single-
            column layout below xl) in the original top-to-bottom order;
            only the wide-screen VISUAL placement changes. Sticky relies on
            SheetContent's own scroll (this div has no independent scroll
            of its own). */}
        <div
          className={cn(
            'px-5 sm:px-6 py-5 xl:grid xl:items-start xl:gap-5',
            // The full page has room for a wider money rail, so the
            // contract figures never clip.
            fullPage ? 'xl:grid-cols-[1fr_440px]' : 'xl:grid-cols-[1fr_320px]'
          )}
        >
          <div className="space-y-4 xl:order-2 xl:sticky xl:top-4">
          {/* Contract vs Actual */}
          <Section title={t('jobCosting.detail.section.contractVsActual')}>
            <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-2 gap-3">
              <StatBlock
                label={t('jobCosting.detail.contract')}
                value={formatUsd(row.contract)}
                testId="costing-detail-contract"
              />
              <StatBlock
                label={t('jobCosting.detail.changeOrders')}
                value={formatUsd(row.co_total)}
                testId="costing-detail-co"
              />
              <StatBlock
                label={t('jobCosting.detail.revenue')}
                value={formatUsd(row.revenue)}
                testId="costing-detail-revenue"
              />
              <StatBlock
                label={t('jobCosting.detail.actualCost')}
                value={formatUsd(row.actualCost)}
                tone="warning"
                testId="costing-detail-actual"
              />
            </div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
              <StatBlock
                label={t('jobCosting.detail.grossProfit')}
                value={formatUsd(row.grossProfit)}
                tone={row.grossProfit < 0 ? 'danger' : 'success'}
                testId="costing-detail-profit"
              />
              <StatBlock
                label={t('jobCosting.detail.margin')}
                value={formatPct(row.marginPct)}
                tone={row.tone}
                testId="costing-detail-margin"
              />
              <StatBlock
                label={t('jobCosting.detail.budgetVariance')}
                value={formatUsd(row.budgetVariance)}
                tone={row.budgetVariance < 0 ? 'danger' : 'success'}
                testId="costing-detail-variance"
              />
            </div>
          </Section>

          {/* Variance analysis */}
          <Section title={t('jobCosting.detail.section.varianceAnalysis')}>
            <div className="space-y-2">
              <VarianceRow
                label={t('jobCosting.detail.laborRow')}
                actual={row.labor.cost}
                budget={row.budget * 0.4}
                Icon={HardHat}
              />
              <VarianceRow
                label={t('jobCosting.detail.materialsRow')}
                actual={row.materials.total}
                budget={row.budget * 0.35}
                Icon={Wrench}
              />
              <VarianceRow
                label={t('jobCosting.detail.otherRow')}
                actual={Math.max(0, row.actualCost - row.labor.cost - row.materials.total)}
                budget={row.budget * 0.1}
                Icon={Receipt}
              />
            </div>
            <div className="mt-2 text-[11px] text-muted-foreground">
              {t('jobCosting.detail.varianceFootnote')}
            </div>
          </Section>
          </div>

          <div className="space-y-4 mt-4 xl:mt-0 xl:order-1 xl:min-w-0">
          {/* Labor detail */}
          <Section
            title={t('jobCosting.labor.title', { count: row.labor.entries.length })}
            right={
              <span className="text-[11px] text-muted-foreground text-right">
                {t('jobCosting.labor.stats', {
                  hours: (row.labor.hours || 0).toFixed(2),
                  cost: formatUsd(row.labor.cost),
                })}
                {row.labor.sub?.hours > 0 ? (
                  <span className="block" data-testid="costing-detail-labor-split">
                    {t('jobCosting.labor.split', {
                      defaultValue:
                        'Crew {{crewHours}}h · {{crewCost}} — Sub {{subHours}}h · {{subCost}}',
                      crewHours: (row.labor.crew.hours || 0).toFixed(2),
                      crewCost: formatUsd(row.labor.crew.cost),
                      subHours: (row.labor.sub.hours || 0).toFixed(2),
                      subCost: formatUsd(row.labor.sub.cost),
                    })}
                  </span>
                ) : null}
              </span>
            }
          >
            {laborGroups.length === 0 ? (
              <div className="text-sm text-muted-foreground py-4 text-center">
                {t('jobCosting.labor.empty')}
              </div>
            ) : (
              <div className="space-y-2" data-testid="costing-detail-labor">
                {laborGroups.slice(0, 8).map((g) => (
                  <div
                    key={g.date}
                    className="flex items-center justify-between border border-border rounded-md bg-[hsl(var(--muted))]/40 px-3 py-2"
                  >
                    <div>
                      <div className="text-xs font-semibold text-foreground">{g.date}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {g.entries
                          .map((e) =>
                            e.name && e.worker_type === 'sub'
                              ? `${e.name} (${t('jobCosting.labor.subTag', { defaultValue: 'sub' })})`
                              : e.name
                          )
                          .filter(Boolean)
                          .join(', ') || t('jobCosting.labor.crewFallback')}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-foreground tabular-nums">
                        {formatUsd(g.cost)}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {g.hours.toFixed(2)}h
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Materials detail */}
          <Section
            title={t('jobCosting.materials.title', { count: materials.length })}
            right={
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground">
                  {formatUsd(row.materials.total)}
                </span>
                <Button
                  size="sm"
                  onClick={() => onLogMaterial?.(row.job)}
                  className="h-8 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-[10px]"
                  data-testid="costing-detail-log-material"
                >
                  <Plus size={12} className="mr-1.5" />
                  {t('jobCosting.materials.logMaterialBtn')}
                </Button>
              </div>
            }
          >
            {materials.length === 0 ? (
              <div className="text-sm text-muted-foreground py-4 text-center">
                {t('jobCosting.materials.empty')}
              </div>
            ) : (
              <div className="space-y-2" data-testid="costing-detail-materials">
                {materials.slice(0, 20).map((m) => (
                  <div
                    key={m.id}
                    className="flex items-center justify-between gap-3 border border-border rounded-md bg-[hsl(var(--muted))]/40 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-foreground truncate">
                        {m.store || t('jobCosting.materials.store')}
                        {m.desc ? <span className="text-muted-foreground font-normal"> {'\u2014'} {m.desc}</span> : null}
                      </div>
                      <div className="text-[11px] text-muted-foreground">{m.date}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-sm font-semibold text-foreground tabular-nums">
                        {formatUsd(m.amount)}
                      </span>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleDelete(m.id)}
                        disabled={deletingId === m.id}
                        className="h-7 w-7 text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10"
                        aria-label={t('jobCosting.materials.removeAria')}
                        data-testid={`costing-material-delete-${m.id}`}
                      >
                        {deletingId === m.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 size={12} />
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>
          </div>
        </div>
    </>
  );

  if (fullPage) {
    return (
      <div
        className="rounded-lg border border-[hsl(var(--hairline))] bg-[hsl(var(--background))] overflow-hidden"
        data-testid="costing-detail-sheet"
      >
        {content}
      </div>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl xl:max-w-4xl overflow-y-auto bg-[hsl(var(--background))] border-l border-[hsl(var(--hairline))] p-0"
        data-testid="costing-detail-sheet"
      >
        {content}
      </SheetContent>
    </Sheet>
  );
};

const VarianceRow = ({ label, actual, budget, Icon }) => {
  const { t } = useTranslation();
  const diff = budget - actual;
  const positive = diff >= 0;
  return (
    <div className="flex items-center justify-between gap-3 border border-border rounded-md bg-[hsl(var(--muted))]/40 px-3 py-2">
      <div className="flex items-center gap-2">
        {Icon ? <Icon size={12} className="text-[hsl(var(--primary))]/80" /> : null}
        <span className="text-xs font-semibold text-foreground">{label}</span>
      </div>
      <div className="flex items-center gap-4 text-[11px]">
        <span className="text-muted-foreground">
          {t('jobCosting.detail.budgetShort')} {formatUsd(budget)}
        </span>
        <span className="text-foreground">
          {t('jobCosting.detail.actualShort')} {formatUsd(actual)}
        </span>
        <span
          className={cn(
            'inline-flex items-center gap-1 font-semibold',
            positive ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--destructive))]'
          )}
        >
          {positive ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
          {formatUsd(Math.abs(diff))}
        </span>
      </div>
    </div>
  );
};

export default JobCostingDetailSheet;
